import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Camera, Upload, RefreshCw, X, RotateCw, Check, ArrowLeft,
  AlertTriangle, SwitchCamera, Maximize2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { generatePhotoVariants, loadImage } from "@/lib/patientPhoto";

// Max upload size (client-side gate — matches requirement #3).
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";

/**
 * A full-featured patient-photo dialog: camera capture, gallery upload,
 * live crop with zoom/pan/rotate, three-size WebP output.
 *
 * Camera access is *only* requested when the user actively enters camera
 * mode — never on mount — so we don't scare the user with a permission
 * prompt unless they explicitly ask for it.
 */
export default function PatientPhotoDialog({
  open, onClose, onSave, initialMode = "chooser", patientName,
}) {
  // Stages: "chooser" | "camera" | "cropping"
  const [stage, setStage] = useState(initialMode);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Camera state
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState(null);

  // Cropper state
  const cropRef = useRef(null);
  const [rawImage, setRawImage] = useState(null);  // decoded HTMLImageElement
  const [rawSrc, setRawSrc] = useState(null);       // dataURL for preview
  const [zoom, setZoom] = useState(1);              // 1..3
  const [rotate, setRotate] = useState(0);          // 0/90/180/270
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // pan in DISPLAY px
  const dragRef = useRef(null);

  // ----- reset ---------------------------------------------------------------
  const reset = useCallback(() => {
    stopCamera();
    setStage(initialMode);
    setError(null);
    setRawImage(null); setRawSrc(null);
    setZoom(1); setRotate(0); setOffset({ x: 0, y: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMode]);

  useEffect(() => {
    if (open) {
      setStage(initialMode);
      setError(null);
      if (initialMode === "camera") startCamera();
      // eslint-disable-next-line no-use-before-define
      else if (initialMode === "gallery") pickFile();
    } else {
      reset();
    }
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMode]);

  // ----- Camera --------------------------------------------------------------
  async function startCamera(preferId = null) {
    setStage("camera");
    setError(null);
    // Feature detection — some in-browser previews block getUserMedia entirely.
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("Your browser doesn't support camera capture. Please upload a photo from your gallery.");
      return;
    }
    try {
      const constraints = {
        video: preferId
          ? { deviceId: { exact: preferId } }
          : { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => { /* iOS autoplay guard */ });
      }
      // Enumerate other cameras (labels populate only after permission is granted).
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const cams = all.filter((d) => d.kind === "videoinput");
        setDevices(cams);
        const active = stream.getVideoTracks()[0]?.getSettings?.().deviceId
          || preferId || cams[0]?.deviceId || null;
        setDeviceId(active);
      } catch { /* silent — enumeration is best-effort */ }
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setError("Camera access is blocked. Please allow camera permissions or upload a photo from your gallery.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setError("No camera was found on this device. Please upload a photo from your gallery instead.");
      } else {
        setError("Couldn't start the camera. Please try again or upload from your gallery.");
      }
    }
  }

  function stopCamera() {
    streamRef.current?.getTracks()?.forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }

  async function switchCamera() {
    if (devices.length < 2) return;
    const idx = devices.findIndex((d) => d.deviceId === deviceId);
    const next = devices[(idx + 1) % devices.length];
    stopCamera();
    await startCamera(next.deviceId);
  }

  async function capture() {
    const v = videoRef.current;
    if (!v || v.readyState < 2) return toast.error("Camera isn't ready yet");
    const w = v.videoWidth, h = v.videoHeight;
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    // Mirror the front-facing preview so the saved photo looks natural.
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.translate(w, 0); ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, w, h);
    ctx.restore();
    const dataUrl = canvas.toDataURL("image/webp", 0.92);
    stopCamera();
    const img = await loadImage(dataUrl);
    setRawSrc(dataUrl); setRawImage(img);
    setStage("cropping");
    setZoom(1); setRotate(0); setOffset({ x: 0, y: 0 });
  }

  // ----- Gallery -------------------------------------------------------------
  const fileInputRef = useRef(null);
  function pickFile() {
    fileInputRef.current?.click();
  }
  async function onFileChosen(file) {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`That image is ${(file.size / 1024 / 1024).toFixed(1)} MB — the max is 10 MB.`);
      return;
    }
    if (!ACCEPT.split(",").includes(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
      toast.error("Please choose a JPG, PNG, WEBP or HEIC image.");
      return;
    }
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result); r.onerror = rej;
      r.readAsDataURL(file);
    });
    try {
      const img = await loadImage(dataUrl);
      setRawSrc(dataUrl); setRawImage(img);
      setStage("cropping");
      setZoom(1); setRotate(0); setOffset({ x: 0, y: 0 });
    } catch {
      toast.error("Could not read that image. HEIC files from iPhones sometimes need conversion — try JPG or PNG.");
    }
  }

  // ----- Cropper -------------------------------------------------------------
  // Displayed cropper viewport is 320×320. The user pans/zooms/rotates a copy
  // of the raw image. On save, we translate the display-space transforms into
  // image-space crop coordinates.
  //
  // Cropper defaults to "contain / fit" — the ENTIRE image is visible inside
  // the viewport at zoom=1. This matches WhatsApp / LinkedIn / Google profile
  // editors: users see the whole photo first, then choose to zoom in.
  const VIEWPORT = 320;

  // Compute the fit-size (in display px) so we can render the image with
  // explicit width/height at zoom=1 — no object-fit trickery, no auto-crop.
  const imgFit = useMemo(() => {
    if (!rawImage) return { w: VIEWPORT, h: VIEWPORT };
    const nW = rawImage.naturalWidth || 1;
    const nH = rawImage.naturalHeight || 1;
    const s = VIEWPORT / Math.max(nW, nH);
    return { w: nW * s, h: nH * s };
  }, [rawImage]);

  const onDragStart = (e) => {
    e.preventDefault();
    const pt = "touches" in e ? e.touches[0] : e;
    dragRef.current = { x: pt.clientX, y: pt.clientY, ox: offset.x, oy: offset.y };
  };
  const onDragMove = (e) => {
    if (!dragRef.current) return;
    const pt = "touches" in e ? e.touches[0] : e;
    const dx = pt.clientX - dragRef.current.x;
    const dy = pt.clientY - dragRef.current.y;
    setOffset({ x: dragRef.current.ox + dx, y: dragRef.current.oy + dy });
  };
  const onDragEnd = () => { dragRef.current = null; };

  async function save() {
    if (!rawImage) return;
    setSaving(true);
    try {
      // Convert display transforms to image-space crop.
      // Baseline "fit-to-viewport" scale (contain, not cover) — this matches
      // the initial render where the entire image is visible at zoom=1.
      const natW = rawImage.naturalWidth, natH = rawImage.naturalHeight;
      const isSideways = rotate % 180 !== 0;
      const rW = isSideways ? natH : natW;
      const rH = isSideways ? natW : natH;
      // Contain-scale: shrink so the LARGEST dimension fits the viewport.
      const containScale = VIEWPORT / Math.max(rW, rH);
      const effectiveScale = containScale * zoom;
      // In image-space, the crop rect side = VIEWPORT / effectiveScale
      const cropSide = VIEWPORT / effectiveScale;
      // Centre of viewport, in image-space, offset by the user's pan.
      // Pan is in DISPLAY px → divide by effectiveScale to convert to image px.
      const cxDisplay = -offset.x / effectiveScale;
      const cyDisplay = -offset.y / effectiveScale;
      // Rotate the pan back into unrotated image coords.
      const theta = -(rotate * Math.PI) / 180;
      const cxR = cxDisplay * Math.cos(theta) - cyDisplay * Math.sin(theta);
      const cyR = cxDisplay * Math.sin(theta) + cyDisplay * Math.cos(theta);
      const cx = natW / 2 + cxR;
      const cy = natH / 2 + cyR;
      const variants = await generatePhotoVariants(rawImage, {
        cx, cy, size: cropSide, rotate,
      });
      await onSave(variants);
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("Could not save the photo. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  // Fit: reset zoom to 1 (the initial "entire image visible" state) and re-center.
  const resetToFit = () => { setZoom(1); setOffset({ x: 0, y: 0 }); };

  // ----- render --------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg p-0 overflow-hidden" data-testid="patient-photo-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#E8EAE9]">
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Camera className="w-5 h-5 text-clay" />
            {stage === "camera" && "Take photo"}
            {stage === "cropping" && "Adjust & save"}
            {stage === "chooser" && "Patient photo"}
          </DialogTitle>
          {patientName && (
            <div className="text-xs text-ink-mid">{patientName}</div>
          )}
        </DialogHeader>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          data-testid="photo-file-input"
          onChange={(e) => onFileChosen(e.target.files?.[0])}
        />

        {/* Chooser (only used if the caller opens the dialog without a mode) */}
        {stage === "chooser" && (
          <div className="p-5 space-y-3">
            <button
              data-testid="photo-choose-camera"
              onClick={() => startCamera()}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border border-[#E8EAE9] hover:bg-linen text-left"
            >
              <Camera className="w-5 h-5 text-clay" />
              <div>
                <div className="text-sm font-medium text-ink">Take photo</div>
                <div className="text-xs text-ink-soft">Use this device's camera to capture a new photo</div>
              </div>
            </button>
            <button
              data-testid="photo-choose-gallery"
              onClick={pickFile}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border border-[#E8EAE9] hover:bg-linen text-left"
            >
              <Upload className="w-5 h-5 text-pine" />
              <div>
                <div className="text-sm font-medium text-ink">Upload from gallery</div>
                <div className="text-xs text-ink-soft">JPG, PNG, WEBP or HEIC · up to 10 MB</div>
              </div>
            </button>
          </div>
        )}

        {/* Camera preview */}
        {stage === "camera" && (
          <div className="p-5">
            {error ? (
              <div className="rounded-2xl bg-[#FCECEC] border border-[#EBBDBB] p-4 flex gap-3 items-start" data-testid="camera-error">
                <AlertTriangle className="w-5 h-5 text-[#B94A48] shrink-0 mt-0.5" />
                <div className="text-sm text-[#7A2A2E]">
                  {error}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={pickFile}
                      data-testid="camera-fallback-gallery"
                      className="text-xs px-3 py-1.5 rounded-full bg-pine text-linen hover:bg-pine/90 inline-flex items-center gap-1.5"
                    >
                      <Upload className="w-3.5 h-3.5" /> Upload from gallery
                    </button>
                    <button
                      onClick={() => startCamera()}
                      className="text-xs px-3 py-1.5 rounded-full border border-[#E8EAE9] text-ink hover:bg-linen"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="relative bg-black rounded-2xl overflow-hidden aspect-square">
                  <video
                    ref={videoRef}
                    playsInline
                    autoPlay
                    muted
                    data-testid="camera-preview"
                    className="w-full h-full object-cover"
                    style={{ transform: "scaleX(-1)" /* mirror for natural framing */ }}
                  />
                  {devices.length > 1 && (
                    <button
                      onClick={switchCamera}
                      data-testid="camera-switch"
                      title="Switch camera"
                      className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-ink grid place-items-center shadow"
                    >
                      <SwitchCamera className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <button
                    onClick={onClose}
                    data-testid="camera-cancel"
                    className="text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-linen inline-flex items-center gap-1.5"
                  >
                    <X className="w-4 h-4" /> Cancel
                  </button>
                  <button
                    onClick={capture}
                    data-testid="camera-capture"
                    className="text-sm px-5 py-2.5 rounded-full bg-clay text-linen hover:brightness-110 inline-flex items-center gap-2 shadow"
                  >
                    <Camera className="w-4 h-4" /> Capture
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Cropping */}
        {stage === "cropping" && rawSrc && (
          <div className="p-5">
            <div
              ref={cropRef}
              className="relative mx-auto bg-[#EDEEEE] overflow-hidden rounded-full cursor-move select-none touch-none"
              style={{ width: VIEWPORT, height: VIEWPORT }}
              onMouseDown={onDragStart}
              onMouseMove={onDragMove}
              onMouseUp={onDragEnd}
              onMouseLeave={onDragEnd}
              onTouchStart={onDragStart}
              onTouchMove={onDragMove}
              onTouchEnd={onDragEnd}
              data-testid="photo-crop-viewport"
            >
              <img
                src={rawSrc}
                alt="Preview"
                draggable={false}
                className="absolute top-1/2 left-1/2 pointer-events-none"
                style={{
                  transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) rotate(${rotate}deg) scale(${zoom})`,
                  transformOrigin: "center center",
                  width: imgFit.w,
                  height: imgFit.h,
                  maxWidth: "none",
                }}
              />
              {/* Subtle inner ring so users see the crop boundary */}
              <div className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-black/10" />
            </div>

            {/* Zoom / rotate controls */}
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-widest text-ink-soft w-10">Zoom</span>
                <input
                  type="range"
                  min={1} max={3} step={0.05}
                  value={zoom}
                  data-testid="photo-zoom"
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 accent-clay"
                />
                <button
                  onClick={resetToFit}
                  data-testid="photo-fit"
                  className="text-xs px-3 py-1.5 rounded-full border border-[#E8EAE9] hover:bg-linen inline-flex items-center gap-1.5"
                  title="Fit — show the entire image"
                >
                  <Maximize2 className="w-3.5 h-3.5" /> Fit
                </button>
                <button
                  onClick={() => setRotate((r) => (r + 90) % 360)}
                  data-testid="photo-rotate"
                  className="text-xs px-3 py-1.5 rounded-full border border-[#E8EAE9] hover:bg-linen inline-flex items-center gap-1.5"
                  title="Rotate 90°"
                >
                  <RotateCw className="w-3.5 h-3.5" /> Rotate
                </button>
              </div>
              <div className="text-[11px] text-ink-soft flex items-center gap-1.5">
                <span>The full photo is shown by default — zoom in, drag to reposition, or rotate as needed.</span>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between gap-2">
              <button
                onClick={() => { setRawImage(null); setRawSrc(null); startCamera(); }}
                data-testid="photo-retake"
                className="text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-linen inline-flex items-center gap-1.5"
              >
                <ArrowLeft className="w-4 h-4" /> Retake
              </button>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  data-testid="photo-cancel"
                  className="text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-linen inline-flex items-center gap-1.5"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  data-testid="photo-save"
                  className="text-sm px-4 py-2 rounded-full bg-pine text-linen hover:bg-pine/90 inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Save photo
                </button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
